// client/src/pages/admin/AdminDocumentList.js
// One list page for each kind of manually created document - mounted three
// times (invoices, quotes, proforma invoices) with a different `type`.
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Stack,
  Alert, CircularProgress, Select, MenuItem, Chip, TextField, InputAdornment,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { AuthContext } from '../../context/AuthContext';
import EmptyState from '../../components/EmptyState';
import { formatCurrency } from '../../utils/currency';
import { API_BASE_URL } from '../../utils/media';
import {
  DOCUMENT_TYPES, STATUS_COLORS, statusLabel, formatDocumentDate, isLockedDocument,
} from '../../utils/documents';

const AdminDocumentList = ({ type }) => {
  const meta = DOCUMENT_TYPES[type];
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState('');
  const [search, setSearch] = useState('');

  const fetchDocuments = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/documents?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Failed to load ${meta.plural.toLowerCase()}.`);
      setDocuments(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, type, meta.plural]);

  useEffect(() => {
    setLoading(true);
    setSearch('');
    fetchDocuments();
  }, [fetchDocuments]);

  const handleStatusChange = async (doc, status) => {
    if (doc.type === 'invoice' && status === 'cancelled'
      && !window.confirm(`Cancel invoice ${doc.number}? A cancelled invoice can't be edited or reopened.`)) return;
    setActionError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/documents/${doc.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update status.');
      fetchDocuments();
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete ${meta.label.toLowerCase()} ${doc.number}? This cannot be undone.`)) return;
    setActionError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/documents/${doc.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete.');
      fetchDocuments();
    } catch (err) {
      setActionError(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const query = search.trim().toLowerCase();
  const visible = query
    ? documents.filter((d) => [d.number, d.customer_name, d.customer_company, d.customer_email]
      .some((value) => value && value.toLowerCase().includes(query)))
    : documents;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h5" fontWeight={700}>{type === 'invoice' ? 'Invoices made by hand' : meta.plural}</Typography>
        <Button variant="contained" component={Link} to={`/admin/documents/new/${type}`}>New {meta.label}</Button>
      </Stack>

      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}

      {documents.length === 0 ? (
        <EmptyState
          icon={<ReceiptLongOutlinedIcon />}
          title={`No ${meta.plural.toLowerCase()} yet`}
          description={`Create a ${meta.label.toLowerCase()} by hand for a customer - it won't affect stock or online orders.`}
          actionLabel={`New ${meta.label}`}
          onAction={() => navigate(`/admin/documents/new/${type}`)}
        />
      ) : (
        <>
          <TextField
            size="small" placeholder="Search by number or customer" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2, width: { xs: '100%', sm: 320 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Number</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>{meta.dueLabel}</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{doc.number}</Typography>
                      {doc.source_number && (
                        <Typography variant="caption" color="text.secondary">from {doc.source_number}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {doc.customer_name}
                      {doc.customer_company && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{doc.customer_company}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDocumentDate(doc.issue_date)}</TableCell>
                    <TableCell>{formatDocumentDate(doc.due_date)}</TableCell>
                    <TableCell align="right">{formatCurrency(doc.total_amount)}</TableCell>
                    <TableCell>
                      {doc.type === 'invoice' && doc.status === 'cancelled' ? (
                        <Chip label="Cancelled" size="small" />
                      ) : (
                        <Select
                          size="small" value={doc.status} variant="standard" disableUnderline
                          onChange={(e) => handleStatusChange(doc, e.target.value)}
                          renderValue={(value) => <Chip label={statusLabel(value)} size="small" color={STATUS_COLORS[value]} />}
                        >
                          {meta.statuses.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" component={Link} to={`/documents/${doc.id}`}>View</Button>
                      {!isLockedDocument(doc) && (
                        <Button size="small" component={Link} to={`/admin/documents/${doc.id}/edit`}>Edit</Button>
                      )}
                      {type !== 'invoice' && (
                        <Button size="small" color="error" onClick={() => handleDelete(doc)}>Delete</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {visible.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center">No matches.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default AdminDocumentList;
